import React from "react";

/**
 * Wave Loader Component
 * Creative, engaging loading states with wave animations
 */
const WaveLoader = ({
  size = "default", // "small", "default", "large"
  variant = "default", // "default", "bars", "dots", "lines"
  color = "emerald", // "emerald", "blue", "purple", "orange"
  className = "",
  count = 5,
  speed = "normal" // "slow", "normal", "fast"
}) => {
  // Size configurations
  const sizeConfig = {
    small: {
      bar: "w-1 h-3",
      dot: "w-1.5 h-1.5",
      line: "w-0.5 h-4",
      gap: "gap-0.5"
    },
    default: {
      bar: "w-1.5 h-4",
      dot: "w-2 h-2",
      line: "w-1 h-5",
      gap: "gap-1"
    },
    large: {
      bar: "w-2 h-6",
      dot: "w-3 h-3",
      line: "w-1.5 h-7",
      gap: "gap-1.5"
    }
  };

  const config = sizeConfig[size];

  // Color configurations
  const colorConfig = {
    emerald: "bg-emerald-500",
            blue: "bg-emerald-500",
    purple: "bg-purple-500",
    orange: "bg-orange-500"
  };

  const colorClass = colorConfig[color];

  // Speed configurations
  const speedConfig = {
    slow: "1.5s",
    normal: "1s",
    fast: "0.6s"
  };

  const animationDuration = speedConfig[speed];

  // Variant renderers
  const renderBars = () => (
    <div className={`flex items-end ${config.gap}`}>
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          className={`${config.bar} ${colorClass} rounded-full animate-pulse`}
          style={{
            animationDelay: `${index * 0.1}s`,
            animationDuration: animationDuration
          }}
        ></div>
      ))}
    </div>
  );

  const renderDots = () => (
    <div className={`flex items-center ${config.gap}`}>
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          className={`${config.dot} ${colorClass} rounded-full animate-bounce`}
          style={{
            animationDelay: `${index * 0.15}s`,
            animationDuration: animationDuration
          }}
        ></div>
      ))}
    </div>
  );

  const renderLines = () => (
    <div className={`flex items-center ${config.gap}`}>
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          className={`${config.line} ${colorClass} rounded-full animate-pulse`}
          style={{
            animationDelay: `${index * 0.08}s`,
            animationDuration: animationDuration
          }}
        ></div>
      ))}
    </div>
  );

  // Render based on variant
  const renderLoader = () => {
    switch (variant) {
      case "bars":
        return renderBars();
      case "dots":
        return renderDots();
      case "lines":
        return renderLines();
      default:
        return renderBars();
    }
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      {renderLoader()}
    </div>
  );
};

export default WaveLoader;
